'use client';
import { useTexture } from '@react-three/drei';
import { type ThreeEvent, useThree, type Size } from '@react-three/fiber';
import React, { useEffect, useState } from 'react';
import { SRGBColorSpace, type Texture, TextureLoader } from 'three';
import { screenSize } from '@/hooks/use-is-mobile';
import { useMediaQuery } from '@/hooks/use-media-query';
import { type Media } from '@/payload-types';

function getBasemapUrl(
  image: Media,
  breakpoints: {
    lg: boolean;
    md: boolean;
  },
): string {
  if (process.env.NODE_ENV === 'development') {
    return image?.url as string;
  }
  let width;
  const params = ['quality=50', 'format=auto', 'fit=scale-down'];
  // https://developers.cloudflare.com/images/transform-images/transform-via-url/#recommended-image-sizes
  if (breakpoints.lg) {
    width = image.width;
  } else if (breakpoints.md) {
    width = 1920;
  } else {
    width = 1440;
  }
  params.push(`width=${width}`);

  const normalizeSrc = (src: string) => (src.startsWith('/') ? src.slice(1) : src);
  return `/cdn-cgi/image/${params.join(',')}/${normalizeSrc(image?.url as string)}`;
}

async function getHighResImageUrl(url: string, width: number): Promise<string> {
  const regex = /\/cdn-cgi\/image\/(.*?)\//;
  const updateParams = ['quality=100', 'format=auto', 'fit=scale-down', `width=${width}`];
  return url.replace(regex, `/cdn-cgi/image/${updateParams.join(',')}/`);
}

type Props = {
  textureUrl: string;
  width?: number;
  onPointerDown?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerUp?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerMove?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
};

const setIdleCallback = (cb: () => void): (() => void) => {
  if (
    typeof window.requestIdleCallback === 'undefined' ||
    typeof window.cancelIdleCallback === 'undefined'
  ) {
    // Fallback for environments that do not support requestIdleCallback
    // This is a simple timeout to ensure the callback is called after a delay
    const timeoutId = setTimeout(cb, 1500);

    return () => clearTimeout(timeoutId);
  }
  const idleCallbackId = window.requestIdleCallback(cb);
  return () => window.cancelIdleCallback(idleCallbackId);
};

const DrawMapInternal: React.FC<Props> = ({
  onPointerDown,
  onPointerMove,
  onPointerUp,
  textureUrl,
  width = 1920,
}) => {
  const { size } = useThree();
  const mapTexture = useTexture(textureUrl);
  const [texture, setTexture] = useState<Texture>(mapTexture);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    if (typeof window !== 'undefined') {
      return setIdleCallback(() => {
        getHighResImageUrl(textureUrl, width)
          .then((url) => {
            const loader = new TextureLoader();
            loader.load(url, (texture) => {
              mapTexture.dispose();
              setTexture(texture);
            });
          })
          .catch((error) => {
            console.error(error);
          });
      });
    }
  }, [mapTexture, textureUrl, width]);

  texture.colorSpace = SRGBColorSpace;

  return (
    <mesh
      position={[0, 0, 0]}
      scale={[size.width, size.height, 1]}
      onPointerDown={(e) => onPointerDown?.(e, size)}
      onPointerMove={(e) => onPointerMove?.(e, size)}
      onPointerUp={(e) => onPointerUp?.(e, size)}
    >
      <planeGeometry />
      <meshBasicMaterial key={textureUrl} map={texture} toneMapped={false} />
    </mesh>
  );
};

export function DrawMap({ basemap, ...props }: { basemap: Media } & Omit<Props, 'textureUrl'>) {
  const lg = useMediaQuery(screenSize.large);
  const md = useMediaQuery(screenSize.medium);
  const xs = useMediaQuery(screenSize.extraSmall);

  if (!lg && !md && !xs) {
    return null;
  }

  const texture = getBasemapUrl(basemap, { lg, md });

  return <DrawMapInternal {...props} textureUrl={texture} width={basemap.width ?? undefined} />;
}
